import React from "react";
import {
  RULE_DEMO_DONE,
  RULE_DEMO_INITIAL,
  RULE_SENTENCE,
  advance,
  caretShown,
  showCard,
  showDone,
  showSim,
  type RuleDemoState,
} from "@/lib/rule-demo";

/**
 * The animated "propose a rule change" card. The loop starts once the card is 30%
 * visible (IntersectionObserver, like the design) and runs the design's exact state
 * machine; with prefers-reduced-motion the card renders its finished state statically.
 * Conditional blocks remount each cycle, replaying their `settle` animation.
 */
export function RuleDemo() {
  const [state, setState] = React.useState<RuleDemoState>(RULE_DEMO_INITIAL);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setState(RULE_DEMO_DONE);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let io: IntersectionObserver | undefined;
    const tick = (current: RuleDemoState) => {
      if (stopped) return;
      const { state: next, delayMs } = advance(current);
      setState(next);
      timer = setTimeout(() => tick(next), delayMs);
    };
    const el = rootRef.current;
    if (el && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            io?.disconnect();
            tick(RULE_DEMO_INITIAL);
          }
        },
        { threshold: 0.3 },
      );
      io.observe(el);
    } else {
      tick(RULE_DEMO_INITIAL);
    }
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      io?.disconnect();
    };
  }, []);

  return (
    <div
      id="rule-demo"
      ref={rootRef}
      style={{ flex: "1.2 1 420px", minWidth: 0, background: "var(--surface)", border: "1px solid rgba(var(--ink-rgb),.11)", borderRadius: "14px", boxShadow: "0 16px 40px -22px rgba(var(--ink-rgb),.25)", padding: "clamp(18px,3vw,26px)", display: "flex", flexDirection: "column", gap: "16px", minHeight: "440px" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "9px", fontSize: "13.5px", fontWeight: 600, color: "var(--ink)" }}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h10"></path><circle cx="17" cy="7" r="2.5"></circle><path d="M20 15h-10"></path><circle cx="7" cy="15" r="2.5"></circle></svg>
          Regelwerk · Änderung vorschlagen
        </span>
        <span style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--muted-2)", border: "1px solid rgba(var(--ink-rgb),.14)", borderRadius: "999px", padding: "3px 10px", whiteSpace: "nowrap", flex: "none" }}>v12 aktiv</span>
      </div>
      <div style={{ background: "var(--surface-quiet)", border: "1px solid rgba(var(--ink-rgb),.1)", borderRadius: "11px", padding: "16px 18px", minHeight: "54px", display: "flex", alignItems: "center" }}>
        <p style={{ margin: 0, fontSize: "clamp(15.5px,1.7vw,17.5px)", fontWeight: 500, color: "var(--ink)", lineHeight: 1.5 }}>
          {RULE_SENTENCE.slice(0, state.typedLen)}
          {caretShown(state) && (
            <span style={{ display: "inline-block", width: "2px", height: "1.15em", background: "var(--accent)", verticalAlign: "text-bottom", marginLeft: "2px", animation: "blink 1.05s step-end infinite" }}></span>
          )}
        </p>
      </div>
      {showCard(state) && (
        <div style={{ border: "1px solid transparent", borderRadius: "11px", overflow: "hidden", animation: "settle .45s cubic-bezier(.2,.7,.3,1) both" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "9px", padding: "11px 16px", background: "var(--accent-soft)" }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}><path d="M5 13l4 4 10-11"></path></svg>
            <span style={{ fontSize: "13.5px", fontWeight: 600, color: "var(--accent)" }}>Änderung verstanden und validiert</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "9px", padding: "14px 16px" }}>
            <div style={{ display: "flex", gap: "12px", fontSize: "14px", lineHeight: 1.5 }}><span style={{ color: "var(--muted-2)", width: "92px", flex: "none" }}>Regel</span><span style={{ fontWeight: 600, color: "var(--ink)" }}>BBQ · Grillmeister-Pflicht</span></div>
            <div style={{ display: "flex", gap: "12px", fontSize: "14px", lineHeight: 1.5 }}><span style={{ color: "var(--muted-2)", width: "92px", flex: "none" }}>Bedingung</span><span style={{ fontWeight: 600, color: "var(--ink)" }}>Leistung: BBQ · Gästezahl ≥ 50</span></div>
            <div style={{ display: "flex", gap: "12px", fontSize: "14px", lineHeight: 1.5 }}><span style={{ color: "var(--muted-2)", width: "92px", flex: "none" }}>Änderung</span><span style={{ fontWeight: 600, color: "var(--ink)" }}>Schwelle 60 <span style={{ color: "var(--faint)" }}>→</span> 50 Gäste</span></div>
          </div>
        </div>
      )}
      {showSim(state) && (
        <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "13px 16px", background: "var(--surface-quiet)", borderRadius: "11px", animation: "settle .4s ease both" }}>
          <span style={{ fontSize: "26px", fontWeight: 600, color: "var(--accent)", fontVariantNumeric: "tabular-nums", minWidth: "34px", textAlign: "center", flex: "none" }}>{state.simN}</span>
          <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.5, color: "var(--text)" }}>von <strong style={{ fontWeight: 600, color: "var(--ink)" }}>128 historischen Anfragen</strong> hätten sich mit dieser Regel anders entschieden.</p>
        </div>
      )}
      {showDone(state) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", animation: "settle .4s ease both" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: "var(--ink)" }}><span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--accent)", flex: "none" }}></span>Bereit zur Aktivierung · v12 → v13</span>
          <span style={{ fontSize: "12.5px", color: "var(--muted-2)" }}>Aktivierung ausschließlich durch einen Menschen</span>
        </div>
      )}
      <p style={{ margin: "auto 0 0", fontSize: "12.5px", color: "var(--faint)" }}>Beispielhafte Darstellung — die Zahlen illustrieren den Mechanismus.</p>
    </div>
  );
}
